import { buildNovuConnectStagingHint, formatNovuConnectCommandForDisplay, getNovuConnectInvocation, getNovuConnectTargetFlags, isNovuStagingApiUrl, normalizeConnectTargetOptions, } from './novu-connect-cli';
export const WEB_CHAT_DOCS_URL = 'https://docs.novu.co/agents/channels/web-chat';
export const AGENT_ONBOARDING_PLAYBOOK_URL = 'https://novu.co/agents.md';
export const NOVU_CONNECT_BRIDGE_RUNTIMES = ['ai-sdk', 'langchain', 'custom-code'];
export function isNovuConnectBridgeRuntime(value) {
    return value === 'ai-sdk' || value === 'langchain' || value === 'custom-code';
}
/** Same default as the dashboard handler scaffold copy when the connector is unknown. */
export const DEFAULT_NOVU_CONNECT_BRIDGE_RUNTIME = 'ai-sdk';
/**
 * CLI `--runtime` for Web Chat copy. Cloud agents store `managed` | `self-hosted`,
 * not the bridge flavor, so missing connector defaults to AI SDK.
 */
export function resolveWebChatConnectRuntime(agentRuntime, connectorId) {
    if (agentRuntime === 'managed') {
        return undefined;
    }
    if (isNovuConnectBridgeRuntime(connectorId)) {
        return connectorId;
    }
    return DEFAULT_NOVU_CONNECT_BRIDGE_RUNTIME;
}
function bridgeRuntimeLabel(runtime) {
    switch (runtime) {
        case 'ai-sdk':
            return 'AI SDK';
        case 'langchain':
            return 'LangChain';
        case 'custom-code':
            return 'custom code';
    }
}
export function buildWebChatTuiCommandParts(apiUrlOrOptions) {
    const options = normalizeConnectTargetOptions(apiUrlOrOptions);
    const runtime = options.runtime && isNovuConnectBridgeRuntime(options.runtime) ? options.runtime : undefined;
    const parts = [
        runtime
            ? `${getNovuConnectInvocation(options.apiUrl)} --runtime ${runtime} --channel web-chat`
            : `${getNovuConnectInvocation(options.apiUrl)} --channel web-chat`,
        ...getNovuConnectTargetFlags(options),
    ];
    const agentIdentifier = options.agentIdentifier?.trim();
    if (agentIdentifier) {
        parts.push(`--agent-identifier ${agentIdentifier}`);
    }
    return parts;
}
export function buildWebChatTuiCommand(apiUrlOrOptions) {
    return buildWebChatTuiCommandParts(apiUrlOrOptions).join(' ');
}
export function buildWebChatTuiCommandForDisplay(apiUrlOrOptions) {
    return formatNovuConnectCommandForDisplay(buildWebChatTuiCommandParts(apiUrlOrOptions));
}
/** Production TUI command. Prefer `buildWebChatTuiCommand(apiUrl)` in the dashboard. */
export const NOVU_CONNECT_WEB_CHAT_TUI_COMMAND = buildWebChatTuiCommand();
export { APPLICATION_IDENTIFIER_PLACEHOLDER, SUBSCRIBER_ID_PLACEHOLDER, } from './connect-embed-prompt-constants';
function signedInDashboardLine(apiUrl) {
    if (isNovuStagingApiUrl(apiUrl)) {
        return `I'm signed in to the Novu staging dashboard, so use dashboard login (not keyless mode).`;
    }
    return `I'm signed in to the Novu dashboard, so use dashboard login (not keyless mode).`;
}
/**
 * Dashboard Copy prompt / Open in Cursor text. Same shape as the onboarding
 * prompt: signed-in line + intent + agents.md. The playbook owns `--ci`,
 * questions, and flags. Staging and local dev use `novu@rc` (staging also passes `--region staging`).
 *
 * When `runtime` is set, this is a dashboard-created bridge agent: one connect
 * run must scaffold the handler and attach Web Chat. Do not ask for runtime or channel again.
 */
export function buildWebChatPrompt(agentName, agentIdentifier, apiUrl, promptOptions) {
    const runtime = promptOptions?.runtime && isNovuConnectBridgeRuntime(promptOptions.runtime) ? promptOptions.runtime : undefined;
    if (runtime) {
        const lines = [
            `${signedInDashboardLine(apiUrl)} Add Web Chat to this project for an existing ${bridgeRuntimeLabel(runtime)} bridge agent following instructions from this markdown file: ${AGENT_ONBOARDING_PLAYBOOK_URL}`,
            buildNovuConnectStagingHint(apiUrl),
            `The dashboard already created "${agentName}" (id: ${agentIdentifier}) with ${bridgeRuntimeLabel(runtime)} and Web Chat selected. Run one \`npx novu connect\` with \`--runtime ${runtime} --channel web-chat --agent-identifier ${agentIdentifier}\`. Do not ask me to pick runtime, channel, or agent. Scaffold or embed in that same run, then start the app with \`npm run dev:novu\`. After connect, read the embed prompt file if printed — integrate Web Chat into this app's existing layout and navigation (not an orphan \`/web-chat\` URL or floating widget).`,
        ].filter((line) => Boolean(line));
        return lines.join('\n\n');
    }
    const lines = [
        `${signedInDashboardLine(apiUrl)} Connect a Novu agent to Web Chat for this project following instructions from this markdown file: ${AGENT_ONBOARDING_PLAYBOOK_URL}`,
        buildNovuConnectStagingHint(apiUrl),
        `The dashboard already has "${agentName}" (id: ${agentIdentifier}) with Web Chat linked for preview. After \`npx novu connect\`, read the embed prompt file if printed — integrate Web Chat into this app's existing layout and navigation (not an orphan \`/web-chat\` URL or floating widget).`,
    ].filter((line) => Boolean(line));
    return lines.join('\n\n');
}
export function buildOnboardingAgentPrompt(apiUrl) {
    const lines = [
        `${signedInDashboardLine(apiUrl)} Connect a Novu agent to Web Chat for this project following instructions from this markdown file: ${AGENT_ONBOARDING_PLAYBOOK_URL}`,
        buildNovuConnectStagingHint(apiUrl),
    ].filter((line) => Boolean(line));
    return lines.join('\n\n');
}
export { buildWebChatEmbedPromptForAuth } from './connect-embed-prompt';
//# sourceMappingURL=web-chat-connect-prompt.js.map